import React from 'react'
import { createRoot } from 'react-dom/client'
import Root from 'src/Root'
import { AppConfig } from 'src/context/AppConfig'
import { createAppContext } from 'src/context/AppContext'
import { coords } from 'src/business/Warehouse/mapPoints'
import { Language } from './api/defs/enums.type'
import { getEnvConfVal, getOptionalEnvConfVal } from 'src/conf/getEnvConfVal'
// import { currentPathAndQuery } from 'src/routing/currentHostUrl'
import TagManager from 'react-gtm-module'

const googleMapsApiKey = getEnvConfVal('GOOGLE_MAPS_API_KEY')
const promoLocations = getOptionalEnvConfVal('PROMO_LOCATIONS')
const firstMonthFreeNewPromoUi = getOptionalEnvConfVal(
    'FIRST_MONTH_FREE_NEW_PROMO_UI'
)
const metaImageUrl = getEnvConfVal('META_IMAGE_URL')
const seoMode = getEnvConfVal('SEO') === 'true'
const chatbotEnable = getEnvConfVal('CHATBOT_ENABLE') === 'true'
const ecomailEnabled = getEnvConfVal('ECOMAIL_ENABLED') === 'true'
const kycEnable = getEnvConfVal('KYC_ENABLE') === 'true'
const gtmId = getEnvConfVal('GTM_ID')
const skLikId = getEnvConfVal('SKLIK_ID')
const formatLocale = getEnvConfVal('FORMAT_LOCALE')
const locales = getEnvConfVal('LOCALES')
const decimalPointDisabled = getOptionalEnvConfVal('DECIMAL_POINT_DISABLED')
const mainCountries = getEnvConfVal('MAIN_COUNTRIES')
const publicUrl = getEnvConfVal('PUBLIC_URL')
const appVerion = getEnvConfVal('APP_VERSION')
const anotherDomainFeUrl = getEnvConfVal('ANOTHER_DOMAIN_FE_URL')
const contactTel = getEnvConfVal('CONTACT_TEL')
const maintenance = getEnvConfVal('MAINTENANCE')
const contactEmail = getEnvConfVal('CONTACT_EMAIL')
const contactWhatsApp = getEnvConfVal('CONTACT_WHATSAPP')
const addressName = getEnvConfVal('ADDRESS_NAME')
const addressStreet = getEnvConfVal('ADDRESS_STREET')
const addressZipCode = getEnvConfVal('ADDRESS_ZIP_CODE')
const addressCity = getEnvConfVal('ADDRESS_CITY')
const addressCountry = getEnvConfVal('ADDRESS_COUNTRY')
const billingIco = getEnvConfVal('BILLING_ICO')
const billingDic = getOptionalEnvConfVal('BILLING_DIC')
const billingIcDhp = getOptionalEnvConfVal('BILLING_IC_DHP')
const billingIban = getEnvConfVal('BILLING_IBAN')
const boxylandCountry = getEnvConfVal('BOXYLAND_COUNTRY')
const proxy = getEnvConfVal('PROXY')

const getDefaultLocale = () => {
    if (formatLocale === 'cs') {
        return Language.CS
    } else if (formatLocale === 'sk') {
        return Language.SK
    } else if (formatLocale === 'en') {
        return Language.EN
    } else if (formatLocale === 'pl') {
        return Language.PL
    } else if (formatLocale === 'hu') {
        return Language.HU
    } else if (formatLocale === 'uk') {
        return Language.UK
    } else if (formatLocale === 'ro') {
        return Language.RO
    } else if (formatLocale === 'it') {
        return Language.IT
    } else if (formatLocale === 'de') {
        return Language.DE
    } else {
        return Language.EN
    }
}

const cfg: AppConfig = {
    googleMapsApiKey,
    promoLocations:
        promoLocations?.split(',').map((id: string) => parseInt(id.trim())) ||
        [],
    firstMonthFreeNewPromoUi: firstMonthFreeNewPromoUi === 'true',
    metaImageUrl,
    defaultCoordinates: coords.Bratislava,
    defaultLocale: getDefaultLocale(),
    locales: locales.split(',').map((l: string) => l.trim() as Language),
    decimalPointDisabled: decimalPointDisabled === 'true',
    mainCountries: mainCountries.split(',').map((c: string) => c.trim()),
    seoMode,
    chatbotEnable,
    ecomailEnabled,
    kycEnable,
    devMode: process.env.NODE_ENV !== 'production',
    googleTagManagerId: gtmId,
    skLikId,
    formatLocale,
    appVerion,
    publicUrl,
    anotherDomainFeUrl,
    contactTel,
    contactEmail,
    contactWhatsApp,
    maintenance: maintenance === 'true',
    address: {
        name: addressName,
        street: addressStreet,
        zipCode: addressZipCode,
        city: addressCity,
        country: addressCountry
    },
    billing: {
        ico: billingIco,
        dic: billingDic,
        icDph: billingIcDhp,
        iban: billingIban
    },
    boxylandCountry,
    proxy: proxy.split(',').map((p: string) => p.trim())
}

const isAndroidNativeRuntime = () => {
    const ua = window.navigator.userAgent || ''
    const win = window as any
    const cap = win?.Capacitor

    if (ua.includes('Android WebView')) return true

    if (cap) {
        try {
            if (typeof cap.getPlatform === 'function') {
                const platform = cap.getPlatform()
                if (platform === 'android') return true
            }
        } catch {}

        if (typeof cap.platform === 'string' && cap.platform === 'android') {
            return true
        }

        if (
            typeof cap.isNativePlatform === 'function' &&
            cap.isNativePlatform()
        ) {
            return /Android/i.test(ua)
        }
    }

    if (win?.androidBridge) return true
    if (/; wv\)/i.test(ua) && /Android/i.test(ua)) return true

    return false
}

const disableSafeAreaForAndroid = () => {
    if (!isAndroidNativeRuntime()) return

    const root = document.documentElement
    root.setAttribute('data-native-platform', 'android')
    root.style.setProperty('--safe-area-top', '0px')
    root.style.setProperty('--safe-area-right', '0px')
    root.style.setProperty('--safe-area-bottom', '0px')
    root.style.setProperty('--safe-area-left', '0px')
}

disableSafeAreaForAndroid()

if (!cfg.devMode) {
    const tagManagerArgs = {
        gtmId: cfg.googleTagManagerId
    }
    console.log('TagManager.initialize(gtmId=' + cfg.googleTagManagerId + ')')
    TagManager.initialize(tagManagerArgs)
} else {
    console.log('cfg.devMode == true, GTM NOT INITIALIZED')
}
const ctx = createAppContext(cfg, window)

const rootEl = document.getElementById('root') as HTMLElement

// if (process.env.NODE_ENV !== 'production') {
//     // eslint-disable-next-line @typescript-eslint/no-var-requires
//     require('../dev/logUnhandledErrors')
// }

createRoot(rootEl).render(<Root ctx={ctx} />)
